/**
 * Strona Kolektora Inwentaryzacyjnego
 * Mobilny interfejs do skanowania kodow kreskowych i zbierania danych inwentaryzacyjnych.
 *
 * Cienki orkiestrator - cala logika biznesowa zyje w useCollectorState,
 * a poszczegolne zakladki w podkomponentach w components/collector/.
 */

import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import {
  useCollectorState,
  CollectorSessions,
  CollectorScanner,
  CollectorItems,
  CollectorPriceView,
  CollectorBuffer,
  QuantityDialog,
} from '../components/collector';

const KolektorPage = () => {
  const state = useCollectorState();

  // Sesja QR (telefon) — ukryj przycisk generowania QR
  const [isQrSession] = useState(() => !!sessionStorage.getItem('v3sys_device_expires_at'));

  // === QR Code dla telefonu ===
  const [showQr, setShowQr] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrCountdown, setQrCountdown] = useState(0);

  const fetchQrCode = useCallback(async () => {
    setQrLoading(true);
    try {
      const res = await api.get('/auth/qr-token');
      if (res.data?.success && res.data?.data) {
        setQrData(res.data.data);
        setQrCountdown(res.data.data.ttl_seconds || 300);
      }
    } catch (e) {
      console.error('QR fetch error:', e);
    } finally {
      setQrLoading(false);
    }
  }, []);

  // Countdown timer dla QR
  useEffect(() => {
    if (!showQr || qrCountdown <= 0) return;
    const timer = setInterval(() => {
      setQrCountdown(prev => {
        if (prev <= 1) {
          fetchQrCode();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [showQr, qrCountdown, fetchQrCode]);

  const handleShowQr = () => {
    if (!showQr) {
      fetchQrCode();
    }
    setShowQr(!showQr);
  };

  const formatCountdown = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const {
    // Location
    selectedLocation,

    // Main state
    activeTab, setActiveTab,
    currentSession,
    items,
    priceBuffer,

    // Online
    isOnline,
    offlineQueueCount,

    // UI
    loading,
    error, setError,
    success,

    // Sync
    syncOfflineData,

    // Quantity dialog
    showQuantityDialog,
    pendingProduct,
    pendingQuantity, setPendingQuantity,
    handleConfirmQuantity,
    handleCancelScan,
  } = state;

  return (
    <div style={{
      padding: '0.75rem',
      backgroundColor: 'var(--bg)',
      minHeight: '100vh',
      maxWidth: '500px',
      margin: '0 auto'
    }}>
      {/* Dialog wprowadzania ilosci */}
      {showQuantityDialog && pendingProduct && (
        <QuantityDialog
          pendingProduct={pendingProduct}
          pendingQuantity={pendingQuantity}
          setPendingQuantity={setPendingQuantity}
          loading={loading}
          handleConfirmQuantity={handleConfirmQuantity}
          handleCancelScan={handleCancelScan}
        />
      )}

      {/* Header */}
      <div style={{
        border: '1px solid var(--purple)', color: 'var(--purple)' ,
        padding: '0.75rem 1rem',
        marginBottom: '0.75rem',
        borderRadius: '8px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <h5 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
            Kolektor
          </h5>
          <div style={{ fontSize: '11px', opacity: 0.9 }}>
            {selectedLocation?.nazwa || 'Brak lokalizacji'}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {/* Przycisk QR dla telefonu — ukryty na sesjach QR (mobilnych) */}
          {!isQrSession && (
            <button
              onClick={handleShowQr}
              style={{
                padding: '6px 12px',
                backgroundColor: showQr ? 'var(--purple)' : 'transparent',
                color: showQr ? '#fff' : 'var(--purple)',
                border: '1px solid var(--purple)',
                fontSize: '12px',
                cursor: 'pointer',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              📱 QR
            </button>
          )}

          {/* Status online */}
          <div style={{
            width: '10px',
            height: '10px',
            borderRadius: '50%',
            backgroundColor: isOnline ? 'var(--success)' : 'var(--danger)'
          }} title={isOnline ? 'Online' : 'Offline'} />

          {/* Kolejka offline */}
          {offlineQueueCount > 0 && (
            <div style={{
              backgroundColor: 'var(--warning)',
              color: 'var(--text)',
              padding: '2px 8px',
              borderRadius: '12px',
              fontSize: '11px',
              fontWeight: '600'
            }}>
              {offlineQueueCount}
            </div>
          )}

          {/* Przycisk sync */}
          {offlineQueueCount > 0 && isOnline && (
            <button
              onClick={syncOfflineData}
              style={{
                padding: '4px 10px',
                backgroundColor: 'var(--card-bg)',
                border: '1px solid var(--border)',
                color: 'var(--text)',
                fontSize: '11px',
                cursor: 'pointer'
              }}
            >
              Sync
            </button>
          )}
        </div>
      </div>

      {/* === Sekcja QR — rozwijana po kliknięciu "📱 QR" (ukryta na sesjach QR) === */}
      {!isQrSession && showQr && (
        <div style={{
          border: '1px solid var(--purple)',
          borderRadius: '8px',
          padding: '1rem',
          marginBottom: '0.75rem',
          textAlign: 'center',
          backgroundColor: 'var(--card-bg)'
        }}>
          {qrLoading && !qrData && (
            <div style={{ padding: '1rem', color: 'var(--text-muted)', fontSize: '13px' }}>
              Generowanie kodu QR...
            </div>
          )}

          {qrData?.qr_data_url && (
            <>
              <div style={{
                background: '#fff',
                display: 'inline-block',
                padding: '10px',
                borderRadius: '10px',
                boxShadow: '0 1px 4px rgba(0,0,0,0.1)'
              }}>
                <img
                  src={qrData.qr_data_url}
                  alt="QR Code"
                  style={{ width: '160px', height: '160px', display: 'block' }}
                />
              </div>
              <p style={{
                color: 'var(--text-muted)',
                fontSize: '12px',
                margin: '10px 0 0',
                lineHeight: 1.5
              }}>
                Zeskanuj telefonem aby otworzyc kolektor
                <br />
                Sesja {Math.round((qrData.session_duration_minutes || 240) / 60)}h
                {qrCountdown > 0 && (
                  <span style={{ color: qrCountdown < 60 ? 'var(--danger)' : 'var(--text-muted)' }}>
                    {' '} | QR wygasa za {formatCountdown(qrCountdown)}
                  </span>
                )}
              </p>
            </>
          )}

          <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'center', gap: '0.5rem' }}>
            <button
              onClick={fetchQrCode}
              disabled={qrLoading}
              style={{
                padding: '6px 12px',
                backgroundColor: 'var(--purple)',
                color: '#fff',
                fontSize: '12px',
                cursor: qrLoading ? 'not-allowed' : 'pointer',
                opacity: qrLoading ? 0.6 : 1
              }}
            >
              Nowy QR
            </button>
            <button
              onClick={() => { setShowQr(false); setQrData(null); }}
              style={{
                padding: '6px 12px',
                backgroundColor: 'var(--card-bg)',
                border: '1px solid var(--border)',
                color: 'var(--text)',
                fontSize: '12px',
                cursor: 'pointer'
              }}
            >
              Zamknij
            </button>
          </div>
        </div>
      )}

      {/* Komunikaty */}
      {error && (
        <div style={{
          backgroundColor: 'var(--danger-light)',
          color: 'var(--danger)',
          padding: '0.75rem',
          borderRadius: '8px',
          marginBottom: '0.75rem',
          fontSize: '13px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          {error}
          <button
            onClick={() => setError('')}
            style={{  background: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: '16px' }}
          >
            &#215;
          </button>
        </div>
      )}

      {success && (
        <div style={{
          backgroundColor: 'var(--success-light)',
          color: 'var(--success)',
          padding: '0.75rem',
          borderRadius: '8px',
          marginBottom: '0.75rem',
          fontSize: '13px'
        }}>
          {success}
        </div>
      )}

      {/* Zakladki */}
      <div style={{
        display: 'flex',
        gap: '0.25rem',
        marginBottom: '0.75rem',
        backgroundColor: 'var(--border)',
        padding: '4px',
        borderRadius: '8px'
      }}>
        <TabButton
          label="Sesje"
          active={activeTab === 'sessions'}
          color="var(--purple)"
          onClick={() => setActiveTab('sessions')}
        />
        <TabButton
          label="Skanuj"
          active={activeTab === 'scan'}
          color="var(--purple)"
          disabled={!currentSession}
          onClick={() => setActiveTab('scan')}
        />
        <TabButton
          label="Pozycje"
          active={activeTab === 'items'}
          color="var(--purple)"
          disabled={!currentSession}
          badge={items.length > 0 ? items.length : null}
          badgeColor="var(--purple)"
          onClick={() => setActiveTab('items')}
        />
        <TabButton
          label="Ceny"
          active={activeTab === 'prices'}
          color="var(--success)"
          onClick={() => setActiveTab('prices')}
        />
        <TabButton
          label="Bufor"
          active={activeTab === 'buffer'}
          color="var(--orange)"
          badge={priceBuffer.length > 0 ? priceBuffer.length : null}
          badgeColor="var(--orange)"
          onClick={() => setActiveTab('buffer')}
        />
      </div>

      {/* Loading */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Ladowanie...</span>
          </div>
        </div>
      )}

      {/* Zawartosc zakladek */}
      {!loading && (
        <>
          {activeTab === 'sessions' && (
            <CollectorSessions
              sessions={state.sessions}
              loading={loading}
              showNewSessionForm={state.showNewSessionForm}
              setShowNewSessionForm={state.setShowNewSessionForm}
              newSessionName={state.newSessionName}
              setNewSessionName={state.setNewSessionName}
              newSessionType={state.newSessionType}
              setNewSessionType={state.setNewSessionType}
              handleCreateSession={state.handleCreateSession}
              handleSelectSession={state.handleSelectSession}
              handleDeleteSession={state.handleDeleteSession}
              locationId={state.locationId}
              selectedInvoiceId={state.selectedInvoiceId}
              setSelectedInvoiceId={state.setSelectedInvoiceId}
            />
          )}
          {activeTab === 'scan' && (
            <CollectorScanner
              currentSession={currentSession}
              items={items}
              scanMode={state.scanMode}
              setScanMode={state.setScanMode}
              isScannerActive={state.isScannerActive}
              setIsScannerActive={state.setIsScannerActive}
              manualEan={state.manualEan}
              setManualEan={state.setManualEan}
              searchQuery={state.searchQuery}
              searchResults={state.searchResults}
              searchLoading={state.searchLoading}
              handleScan={state.handleScan}
              handleManualScan={state.handleManualScan}
              handleSearch={state.handleSearch}
              handleSelectProduct={state.handleSelectProduct}
              setError={setError}
              setSearchResults={() => {}}
            />
          )}
          {activeTab === 'items' && (
            <CollectorItems
              currentSession={currentSession}
              items={items}
              stats={state.stats}
              editingItem={state.editingItem}
              setEditingItem={state.setEditingItem}
              loading={loading}
              handleUpdateItem={state.handleUpdateItem}
              handleDeleteItem={state.handleDeleteItem}
              handlePauseSession={state.handlePauseSession}
              handleConvertToInventory={state.handleConvertToInventory}
              handleCreatePw={state.handleCreatePw}
              handleShowDeliveryReport={state.handleShowDeliveryReport}
            />
          )}

          {/* Raport różnic dostawy */}
          {state.showDeliveryReport && state.deliveryReport && (
            <div style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000,
              display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
              padding: '1rem', overflowY: 'auto'
            }}>
              <div style={{
                backgroundColor: 'var(--card-bg)', borderRadius: '8px',
                width: '100%', maxWidth: '600px', padding: '1rem', marginTop: '2rem'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                  <h6 style={{ margin: 0, fontSize: '14px', fontWeight: '700' }}>
                    Raport kontroli dostawy
                  </h6>
                  <button onClick={() => state.setShowDeliveryReport(false)} style={{ background: 'none', fontSize: '18px', cursor: 'pointer', color: 'var(--text-muted)' }}>×</button>
                </div>

                {state.deliveryReport.invoice && (
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '0.75rem', padding: '0.5rem', backgroundColor: 'var(--bg)', borderRadius: '4px' }}>
                    <strong>{state.deliveryReport.invoice.numer_faktury}</strong> | {state.deliveryReport.invoice.dostawca_nazwa} | {state.deliveryReport.invoice.data_faktury}
                  </div>
                )}

                {/* Podsumowanie */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.5rem', marginBottom: '0.75rem' }}>
                  {[
                    { label: 'Zgodne', value: state.deliveryReport.summary?.matched || 0, color: 'var(--success)' },
                    { label: 'Braki', value: state.deliveryReport.summary?.missing || 0, color: 'var(--danger)' },
                    { label: 'Nadwyżki', value: state.deliveryReport.summary?.excess || 0, color: 'var(--warning)' },
                    { label: 'Nie na fakt.', value: state.deliveryReport.summary?.not_on_invoice || 0, color: 'var(--info)' },
                  ].map(s => (
                    <div key={s.label} style={{ textAlign: 'center', padding: '0.5rem', backgroundColor: 'var(--bg)', borderRadius: '4px', borderLeft: `3px solid ${s.color}` }}>
                      <div style={{ fontSize: '16px', fontWeight: '700', color: s.color }}>{s.value}</div>
                      <div style={{ fontSize: '9px', color: 'var(--text-muted)' }}>{s.label}</div>
                    </div>
                  ))}
                </div>

                {/* Tabela */}
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
                    <thead style={{ position: 'sticky', top: 0, backgroundColor: 'var(--bg)' }}>
                      <tr>
                        {['Produkt', 'Faktura', 'Skan', 'Różnica', 'Status'].map(h => (
                          <th key={h} style={{ padding: '0.4rem', borderBottom: '2px solid var(--border)', textAlign: 'left', fontWeight: '600', fontSize: '10px' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(state.deliveryReport.report || []).map((item, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td style={{ padding: '0.3rem 0.4rem' }}>
                            <div style={{ fontWeight: '500' }}>{item.nazwa}</div>
                            {item.ean && <div style={{ fontSize: '9px', color: 'var(--text-muted)' }}>{item.ean}</div>}
                          </td>
                          <td style={{ padding: '0.3rem 0.4rem' }}>{item.expected_qty}</td>
                          <td style={{ padding: '0.3rem 0.4rem' }}>{item.scanned_qty}</td>
                          <td style={{ padding: '0.3rem 0.4rem' }}>
                            <span style={{
                              fontWeight: '700',
                              color: item.difference === 0 ? 'var(--success)' : item.difference > 0 ? 'var(--warning)' : 'var(--danger)'
                            }}>
                              {item.difference > 0 ? '+' : ''}{item.difference}
                            </span>
                          </td>
                          <td style={{ padding: '0.3rem 0.4rem' }}>
                            <span style={{
                              fontSize: '9px', padding: '2px 6px', borderRadius: '3px', color: '#fff', fontWeight: '600',
                              backgroundColor: item.status === 'ok' ? 'var(--success)' : item.status === 'missing' || item.status === 'not_scanned' ? 'var(--danger)' : item.status === 'excess' ? 'var(--warning)' : 'var(--info)'
                            }}>
                              {item.status === 'ok' ? 'OK' : item.status === 'missing' ? 'BRAK' : item.status === 'not_scanned' ? 'NIE SKAN.' : item.status === 'excess' ? 'NADWYŻKA' : 'SPOZA FAKT.'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <button
                  onClick={() => state.setShowDeliveryReport(false)}
                  style={{ marginTop: '0.75rem', width: '100%', padding: '6px 12px', border: '1px solid var(--text-muted)', fontSize: '12px', cursor: 'pointer' }}
                >
                  Zamknij
                </button>
              </div>
            </div>
          )}

          {activeTab === 'prices' && (
            <CollectorPriceView
              priceScanMode={state.priceScanMode}
              setPriceScanMode={state.setPriceScanMode}
              isPriceScannerActive={state.isPriceScannerActive}
              setIsPriceScannerActive={state.setIsPriceScannerActive}
              priceScannedProduct={state.priceScannedProduct}
              priceEditValue={state.priceEditValue}
              setPriceEditValue={state.setPriceEditValue}
              priceBuffer={priceBuffer}
              manualEan={state.manualEan}
              setManualEan={state.setManualEan}
              priceSearchQuery={state.priceSearchQuery}
              priceSearchResults={state.priceSearchResults}
              priceSearchLoading={state.priceSearchLoading}
              handlePriceScan={state.handlePriceScan}
              handlePriceSearch={state.handlePriceSearch}
              handleSelectPriceProduct={state.handleSelectPriceProduct}
              handleUpdatePrice={state.handleUpdatePrice}
              handleAddToBuffer={state.handleAddToBuffer}
              handleClearPriceProduct={state.handleClearPriceProduct}
              setError={setError}
              loading={loading}
            />
          )}
          {activeTab === 'buffer' && (
            <CollectorBuffer
              priceBuffer={priceBuffer}
              handleRemoveFromBuffer={state.handleRemoveFromBuffer}
              handleClearBuffer={state.handleClearBuffer}
              handlePrintBuffer={state.handlePrintBuffer}
            />
          )}
        </>
      )}
    </div>
  );
};

/**
 * TabButton - przycisk zakladki w pasku nawigacji kolektora.
 */
const TabButton = ({ label, active, color, disabled, badge, badgeColor, onClick }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      flex: 1,
      padding: '6px 12px',
      // Standard V3SYS: aktywna zakladka = pelne tlo primary + bialy tekst
      backgroundColor: active ? 'var(--primary)' : 'var(--card-bg)',
      border: active ? '1px solid var(--primary)' : '1px solid var(--border)',
      color: active ? '#fff' : 'var(--text-secondary)',
      fontSize: '12px',
      fontWeight: active ? '600' : '500',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1
    }}
  >
    {label}
    {badge != null && (
      <span style={{
        marginLeft: '4px',
        backgroundColor: badgeColor || color,
        color: '#fff' ,
        padding: '1px 6px',
        borderRadius: '10px',
        fontSize: '10px'
      }}>
        {badge}
      </span>
    )}
  </button>
);

export default KolektorPage;
